import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { compare } from '@everysens/fast-json-patch';
import { environment } from '@rtls-environment/environment';
import { LocaleService } from '@rtls/core/locale/locale.service';
import { Identifiable } from '@rtls/core/models/base/identifiable.model';
import { Result } from '@rtls/core/models/result.model';

import { Pagination } from '@rtls/core/models/rpc/pagination.model';
import { SearchOperation } from '@rtls/core/models/rpc/search-operation';
import { SearchQuery } from '@rtls/core/models/rpc/search-query';
import { modifyQueryAttributes } from '@rtls/core/models/rpc/search-query.util';
import { Sorting } from '@rtls/core/models/rpc/sorting.model';
import { RtlsModels } from '@rtls/core/models/rtls/rtls-models';
import { RtlsService } from '@rtls/core/models/rtls/rtls-service.model';
import isEmpty from 'lodash-es/isEmpty';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SupportedLocale } from '@rtls/core/locale/locale.enum';

@Injectable()
export class BackendRtlsService<T extends Identifiable> implements RtlsService<T> {
	private baseUrl: string;
	private searchEndPoint: string;

	constructor(
		private _http: HttpClient,
		private localeService: LocaleService,
	) {}

	useModel(type: RtlsModels, baseUrl?: string, searchEndpoint?: string): RtlsService<T> {
		const service = new BackendRtlsService<T>(this._http, this.localeService);
		service.baseUrl = baseUrl ? `${baseUrl}/${type}` : `${environment.thingsApi}/${type}`;
		service.searchEndPoint = searchEndpoint === undefined ? 'search' : '' + searchEndpoint;
		return service;
	}

	get(id: number): Observable<T> {
		return this.wrap(this._http.get<T>(`${this.baseUrl}/${id}`));
	}

	getByIDs(ids: number[]): Observable<Result<T>> {
		const query = new SearchQuery('id', SearchOperation.IN, ids.toString());
		return this.search(query, Pagination.unlimited());
	}

	getAll(pagination?: Pagination): Observable<Result<T>> {
		return this.wrap(
			this._http.get<Result<T>>(`${this.baseUrl}/`, {
				params: this.convert(pagination),
			}),
		);
	}

	getTotalItems(isNullable = false): Observable<number | undefined> {
		const query = isNullable ? new SearchQuery('deletedDate', SearchOperation.IS_NULL) : null;
		return this.search(query, Pagination.default(1, 1)).pipe(map((data: Result<T>) => data.pagination.totalItems));
	}

	search(query: SearchQuery | null, pagination: Pagination, fuzzy: boolean = false): Observable<Result<T>> {
		const fuzzyParams = new HttpParams().set('fuzzy', String(false));
		return this.searchWithParams(query, pagination, fuzzyParams);
	}

	prebuiltSearch(searchTerms: string, pagination = Pagination.unlimited()): Observable<Result<T>> {
		console.warn(`Prebuilt search doesn't work correctly until overriden!`);
		return this.searchWithParams(null, pagination);
	}

	searchWithParams(
		query: SearchQuery | null,
		pagination: Pagination,
		httpParams?: HttpParams,
		httpHeader?: HttpHeaders,
	): Observable<Result<T>> {
		let params = this.convert(pagination);
		// Insert parent params
		if (httpParams != null) {
			httpParams.keys().forEach((key) => (params = params.append(key, String(httpParams.get(key)))));
		}
		const preprocessedQuery = this.preProcess(query);
		return this.wrap(
			this._http.post<Result<T>>(`${this.baseUrl}/${this.searchEndPoint}`, preprocessedQuery, {
				params: params,
				headers: httpHeader,
			}),
		);
	}

	customDownload(uri: string) {
		return this._http.post(`${this.baseUrl}/${uri}`, null, {
			headers: new HttpHeaders({ Accept: 'text/csv;charset=UTF-8' }),
			responseType: 'text',
		});
	}

	downloadResource(
		query: SearchQuery | null,
		pagination: Pagination,
		service: string,
		inBetweenEndpoint?: string,
	): Observable<any> {
		return this._http.post(
			`${this.baseUrl}/${inBetweenEndpoint ? inBetweenEndpoint + '/' : ''}${this.searchEndPoint}${service}`,
			query,
			{
				params: this.convert(pagination),
				headers: new HttpHeaders({ Accept: 'text/csv;charset=UTF-8' }),
				responseType: 'text',
			},
		);
	}

	upsert(original: T, updated: T): Observable<T> {
		if (isEmpty(original)) {
			return this.save(updated);
		}
		return this.update(original, updated);
	}

	update(original: T, updated: T): Observable<T> {
		const id = original.id;

		const jsonPatch = compare(original, updated);
		if (id == null || typeof id !== 'number' || jsonPatch.length === 0) {
			return from([updated]);
		}
		const httpParams = {
			headers: new HttpHeaders().set('Content-Type', 'application/json-patch+json; charset=utf-8'),
		};
		return this.wrap(this._http.patch<T>(`${this.baseUrl}/${id}`, jsonPatch, httpParams));
	}

	save(body: T): Observable<T> {
		return this.wrap(this._http.post<T>(`${this.baseUrl}/`, body));
	}

	saveByPut(body: T, id?: number): Observable<T> {
		return this.wrap(this._http.put<T>(id ? `${this.baseUrl}/${id}` : `${this.baseUrl}`, body));
	}

	saveBatch(body: T[]): Observable<T[]> {
		return this.wrap(this._http.post<T[]>(`${this.baseUrl}/batch`, body));
	}

	delete(id: number, isSoft: boolean = false): Observable<T> {
		let url = `${this.baseUrl}/${id}`;
		if (isSoft === true) {
			url += '?mode=soft';
		}
		return this.wrap(this._http.delete<T>(url));
	}

	deleteAll(isSoft: boolean = true): Observable<boolean> {
		let url = `${this.baseUrl}/search?pageSize=-1`;
		if (isSoft === true) {
			url += '&mode=soft';
		}
		return this.wrap(this._http.delete<boolean>(url));
	}

	// add general behaviour
	wrap<X>(observable: Observable<X>): Observable<X> {
		return observable;
	}

	preProcess(query: SearchQuery | null): SearchQuery | null {
		if (!!query) {
			const suffix = this.localeService.getLanguageCode() === SupportedLocale.FR ? '' : '_en';
			return modifyQueryAttributes(
				query,
				(attr) => attr.endsWith('translation'),
				(attr) => attr + suffix,
			);
		}
		return query;
	}

	// Some search query use the `translation` field => when searching in english it should point to `translation_en`

	protected convert(pagination: Pagination = Pagination.default()): HttpParams {
		const sorting =
			pagination.sorting == null
				? ''
				: pagination.sorting
						.map((sortEntry: Sorting) => `${sortEntry.direction}${sortEntry.attribute}`)
						.join(',');
		return new HttpParams()
			.set('pageNumber', String(pagination.pageNumber))
			.set('pageSize', String(pagination.pageSize))
			.set('sorting', sorting);
	}
}
